/**
 * Route access helpers for business-admin RBAC in the Vue router guard.
 */

import { hasTradieUploadContext } from '@/utils/tradieUploadContext'

export const ROUTE_PERMISSION_PREFIXES = [
  ['home.', 'home.view'],
  ['file.', 'files.view'],
  ['templates.', 'files.upload'],
  ['requests.', 'requests.view'],
  // Specific request detail/view pages only need requests.view — must come
  // before the generic 'request.' catch-all which requires requests.create.
  ['request.view-', 'requests.view'],
  ['request.files-page', 'requests.view'],
  ['request.view-file-page', 'requests.view'],
  ['request.view-message-log-page', 'requests.view'],
  ['request.', 'requests.create'],
  ['tradies.view-tradie', 'tradies.profile.view'],
  ['tradies.', 'tradies.view'],
  ['candidates.view-candidate', 'candidates.profile.view'],
  ['candidates.', 'candidates.view'],
  // Job postings: the specific create/edit pages must come before the generic
  // 'jobs.' catch-all (list + view) — first match wins.
  ['jobs.new-page', 'jobs.create'],
  ['jobs.edit-page', 'jobs.edit'],
  ['jobs.', 'jobs.view'],
  ['projects.', 'projects.view'],
  ['trades.', 'trades.view'],
  ['timesheets.', 'timesheets.view'],
  ['schedule.', 'schedule.view'],
]

export const NAV_LANDING_ROUTES = [
  ['home.welcome-page', 'home.view'],
  ['projects.list-page', 'projects.view'],
  ['file.list-page', 'files.view'],
  ['requests.list-page', 'requests.view'],
  ['tradies.list-page', 'tradies.view'],
  ['candidates.list-page', 'candidates.view'],
  ['trades.list-page', 'trades.view'],
  ['timesheets.list-page', 'timesheets.view'],
  ['schedule.list-page', 'schedule.view'],
  ['jobs.list-page', 'jobs.view'],
]

export function requiredPermissionForRoute(name) {
  if (!name) return null
  // Manual new-upload uses the same permission family as the templates picker.
  if (name === 'file.new-page') return 'files.upload'
  const match = ROUTE_PERMISSION_PREFIXES.find(([prefix]) =>
    name.startsWith(prefix),
  )
  return match ? match[1] : null
}

export function firstAccessibleLanding(can) {
  const hit = NAV_LANDING_ROUTES.find(([, permission]) => can(permission))
  if (hit) return { name: hit[0] }
  return { name: 'user.settings-page', hash: '#profile' }
}

/** Routes used when a BA uploads/creates a file for a tradie (from tradie profile). */
export function isTradieFileUploadRoute(name) {
  if (!name) return false
  if (name.startsWith('templates.')) return true
  return (
    name === 'template.form-page' ||
    name === 'file.new-page' ||
    name === 'file.success-page'
  )
}

export function hasTradieFileUploadContext(route) {
  return hasTradieUploadContext(route)
}

/**
 * Whether a BA may enter a permission-gated route. Tradies and full-access
 * users are handled by the caller; this only extends company-file keys with
 * tradies.files.create for the tradie upload flow.
 */
export function canEnterBusinessAdminRoute(can, routeName) {
  const required = requiredPermissionForRoute(routeName)
  if (!required) return true
  if (can(required)) return true
  // Tradie file upload flow: allow with either the regular or project-context key.
  if (
    isTradieFileUploadRoute(routeName) &&
    (can('tradies.files.create') || can('projects.tradie.files.create'))
  ) {
    return true
  }
  // Tradie file view/edit: allow with tradie-context or project-context key.
  if (
    routeName === 'file.view-page' &&
    (can('tradies.files.view') || can('projects.tradie.files.view'))
  ) {
    return true
  }
  if (
    routeName === 'file.edit-page' &&
    (can('tradies.files.edit') || can('projects.tradie.files.edit'))
  ) {
    return true
  }
  return false
}

export function isProjectScopedTradieFileUploadRoute(can, routeName, route) {
  return (
    isTradieFileUploadRoute(routeName) &&
    hasTradieFileUploadContext(route) &&
    (can('tradies.files.create') || can('projects.tradie.files.create'))
  )
}
